import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Avatar,
    Card,
    CardContent,
    Grid,
    Chip,
} from "@mui/material";
import { styled } from "@mui/system";
import { getCustomerById } from "../services/customers";
import { useLocation, useNavigate } from "react-router-dom";
import { getCustomerAssessmentBySessionAndType } from "../services/customerAssessment";

const FormContainer = styled(Card)({
    background: "white",
    borderRadius: "20px",
    padding: "30px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
    marginBottom: "20px",
});

function ApolloMyAssesments() {
    const role = localStorage.getItem("role");
    const storedCustomer = JSON.parse(localStorage.getItem("customer") || "{}");
    const { customer_id, account_id, assessment_type, preferred_language, customer_session_id } =
        storedCustomer;
    const location = useLocation();
    const pin = location.state?.pin;
    const navigate = useNavigate();

    const handleCardClick = async (item) => {
        if (item.status === "completed") {
            try {
                const response = await getCustomerAssessmentBySessionAndType(
                    customer_session_id,
                    item.key
                );
                console.log("✅ Completed Assessment Data:", response);

                // You can navigate to a detailed view page if needed:
                navigate("/assessment", { state: { data: response, assessment: item } });
            } catch (error) {
                console.error("❌ Failed to fetch assessment data:", error);
                alert("Unable to fetch assessment results. Please try again.");
            }
        }
        // 🟡 Otherwise → go to form to take assessment
        else {
            navigate("/assessment", { state: { assessment: item } });
        }
    };

    // ✅ Parse assessment_type safely
    const parsedAssessmentTypes = (() => {
        try {
            return Array.isArray(JSON.parse(assessment_type))
                ? JSON.parse(assessment_type)
                : [];
        } catch {
            return [];
        }
    })();

    // ✅ Map only matched assessments
    const assessments = parsedAssessmentTypes
        .map((item) => {
            switch (item.title?.toLowerCase()) {
                case "factg7":
                    return {
                        title: "FACT-G7 Assessment",
                        subtitle: "Functional Assessment of Cancer Therapy",
                        key: "factg7",
                        status: item.status || "pending",
                    };
                case "eortc":
                    return {
                        title: "EORTC QLQ-C30",
                        subtitle: "European Quality of Life Questionnaire",
                        key: "eortc",
                        status: item.status || "pending",
                    };
                case "hads":
                    return {
                        title: "HADS Assessment",
                        subtitle: "Hospital Anxiety and Depression Scale",
                        key: "hads",
                        status: item.status || "pending",
                    };
                default:
                    return null;
            }
        })
        .filter(Boolean);
    useEffect(() => {
        if (assessments.length > 0) {
            const allCompleted = assessments.every(
                (item) => item.status === "completed"
            );
            if (allCompleted) {
                navigate("/music-therapy");
            }
        }
    }, [assessments, navigate]);





    const [customer, setCustomer] = useState(null);
    const [user, setUser] = useState("");

    useEffect(() => {
        if (customer_id && account_id) {
            getCustomerById(customer_id, account_id)
                .then((data) => {
                    setCustomer(data);
                    setUser(`${data.firstname || ""} ${data.lastname || ""}`.trim());
                })
                .catch((err) => {
                    console.error("Failed to fetch customer:", err);
                    setUser(
                        `${storedCustomer.firstname || ""} ${storedCustomer.lastname || ""
                            }`.trim()
                    );
                });
        }
    }, [role]);

    // ✅ Function to get chip color based on status
    const getChipColor = (status) => {
        switch (status) {
            case "completed":
                return { label: "Completed", color: "#16a34a" }; // Green
            case "in_process":
                return { label: "In Progress", color: "#2563eb" }; // Blue
            case "pending":
            default:
                return { label: "Pending", color: "#dc2626" }; // Red
        }
    };

    return (
        <FormContainer>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                }}
            >
                <Typography variant="h6" fontWeight={400} sx={{ color: "#1f2937" }}>
                    Apollo   Complete Your Assessments
                </Typography>
            </Box>

            {/* Subtitle */}
            <Typography
                variant="subtitle1"
                sx={{ color: "#4b5563", mb: 3, lineHeight: 1.6 }}
            >
                Welcome, <strong>{user}</strong>! As part of the{" "}
                <strong>Test Arm</strong> protocol, please complete the following
                assessments.
            </Typography>

            {/* ✅ Assessment Cards */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    mb: 4,
                }}
            >
                {assessments.map((item, index) => {
                    const chip = getChipColor(item.status);
                    return (
                        <Box
                            key={index}
                            onClick={() => handleCardClick(item)}
                            sx={{
                                position: "relative",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                p: 2.5,
                                borderRadius: 2,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                backgroundColor: "#fff",
                                border: "1.5px solid #3b82f6",
                                transition: "all 0.3s ease",
                                cursor: "pointer",
                                "&:hover": {
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                    transform: "translateY(-2px)",
                                },
                            }}
                        >
                            {/* ✅ Status Chip */}
                            <Chip
                                label={chip.label}
                                size="small"
                                sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    backgroundColor: chip.color,
                                    color: "#fff",
                                    fontWeight: 500,
                                    fontSize: "0.75rem",
                                }}
                            />

                            <Box>
                                <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                    sx={{ color: "#111827", mb: 0.5 }}
                                >
                                    {item.title}
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#6b7280" }}>
                                    {item.subtitle}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
            {/*  SKIP BUTTON */}

            <Box sx={{ textAlign: "center", mt: 3 }}>
                <button
                    onClick={() => navigate("/music-therapy")}
                    style={{
                        backgroundColor: "#538a36ff",
                        color: "white",
                        padding: "12px 22px",
                        borderRadius: "10px",
                        border: "none",
                        fontSize: "16px",
                        cursor: "pointer",
                        fontWeight: "600"
                    }}
                >
                    Skip to Music
                </button>
            </Box>


        </FormContainer>
    );
}

export default ApolloMyAssesments;
